import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styles from './MaxEventsPerGroup.module.css';
import { ChevronDown, ChevronRight } from 'lucide-react';
import CustomNewButton from 'components/NewDesign/CustomNewButton';

// Count the real, registrable events defined for a group: a positive event code below 1000
// (excludes empty slots, the "-" placeholder code 1200, and system/rest events >= 1000 — matching
// the events a registrant can actually pick via the athlete-events endpoint).
const realEventCount = (group) =>
  (group.events || []).filter((e) => {
    const t = Number(e?.eventType);
    return Number.isFinite(t) && t > 0 && t < 1000;
  }).length;

// §6.4 — Collapsible card (mirrors HighJumpSettings) for setting the max number of events a registrant in each
// program group may sign up for. Shown only for multi-club meets. Reads/writes maxEventsForGroup on each program
// group and emits the full updated groups array via onChange.
const MaxEventsPerGroup = ({ programGroups = [], rotationCount = 0, onChange }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [bulkMaxEvents, setBulkMaxEvents] = useState(''); // '' = No limit

  const hasGroups = programGroups.some((g) => g.displayName);

  // Set one group's max events ('' => no limit)
  const handleMaxChange = useCallback(
    (index, value) => {
      const parsed = value === '' ? null : parseInt(value, 10);
      const updated = programGroups.map((group, i) =>
        i === index ? { ...group, maxEventsForGroup: parsed } : group
      );
      if (onChange) onChange(updated);
    },
    [programGroups, onChange]
  );

  // Apply the "set all" selection to every group. "No limit" clears all; a number is capped per group at that
  // group's own event count (a group can't allow more events than it offers).
  const handleSetAll = useCallback(() => {
    const n = bulkMaxEvents === '' ? null : parseInt(bulkMaxEvents, 10);
    const updated = programGroups.map((group) => {
      if (n === null) return { ...group, maxEventsForGroup: null };
      const count = realEventCount(group);
      return { ...group, maxEventsForGroup: count > 0 ? Math.min(n, count) : null };
    });
    if (onChange) onChange(updated);
  }, [bulkMaxEvents, programGroups, onChange]);

  // Render <option> 1..max
  const numberOptions = (max) =>
    Array.from({ length: Math.max(0, max) }, (_, i) => i + 1).map((n) => (
      <option key={n} value={n}>
        {n}
      </option>
    ));

  return (
    <div className={styles.container}>
      <div
        className={styles.header}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className={styles.headerContent}>
          {isCollapsed ? <ChevronRight size={20} /> : <ChevronDown size={20} />}
          <h3 className={styles.title}>Max Events Per Group</h3>
          <span className={styles.subtitle}>
            Limit how many events each registrant may sign up for
          </span>
        </div>
      </div>

      {!isCollapsed && (
        <div className={styles.content}>
          <div className={styles.setAllRow}>
            <span className={styles.setAllLabel}>Set all groups to</span>
            <select
              className={styles.maxSelect}
              value={bulkMaxEvents}
              onChange={(e) => setBulkMaxEvents(e.target.value)}
            >
              <option value="">No limit</option>
              {numberOptions(rotationCount)}
            </select>
            <CustomNewButton
              onClick={handleSetAll}
              disabled={!hasGroups}
              variant="outline"
              customStyle={{
                backgroundColor: '#4a90e2',
                color: 'white',
                border: '1px solid #4a90e2',
                padding: '6px 14px',
                fontSize: '13px',
              }}
            >
              Set all
            </CustomNewButton>
          </div>

          {!hasGroups ? (
            <div className={styles.emptyState}>
              <p>Add groups in the program above to set event limits.</p>
            </div>
          ) : (
            <div className={styles.list}>
              {programGroups.map((group, index) =>
                group.displayName ? (
                  <div key={group.id || index} className={styles.row}>
                    <span className={styles.groupName}>{group.displayName}</span>
                    <select
                      className={styles.maxSelect}
                      value={group.maxEventsForGroup ?? ''}
                      onChange={(e) => handleMaxChange(index, e.target.value)}
                    >
                      <option value="">No limit</option>
                      {/* up to the group's defined events (or its stored value, if higher) */}
                      {numberOptions(
                        Math.max(
                          realEventCount(group),
                          group.maxEventsForGroup || 0
                        )
                      )}
                    </select>
                  </div>
                ) : null
              )}
            </div>
          )}

          <div className={styles.helpText}>
            <p>
              <strong>💡 Tip:</strong> Pick a number to cap entries, or No limit
              for unlimited. This applies to multi-club meet registration only.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

MaxEventsPerGroup.propTypes = {
  programGroups: PropTypes.arrayOf(
    PropTypes.shape({
      displayName: PropTypes.string,
      maxEventsForGroup: PropTypes.number,
      events: PropTypes.arrayOf(PropTypes.object),
    })
  ),
  rotationCount: PropTypes.number,
  onChange: PropTypes.func,
};

export default MaxEventsPerGroup;
